import { makeAutoObservable, runInAction } from 'mobx'
import { BlueUserStore } from '../store/BlueUserStore'
import { DepartmentStore } from '@evertel/department'
import { DepartmentsAccessStore } from '@evertel/departments-access'
import moment from 'moment'
import { decorate, inject, injectable } from 'inversify'

class ResetPasswordController {
    userId = 0
    nearExpirationDismissedThisSession = false

    constructor(
        private departmentsAccessStore: DepartmentsAccessStore,
        private departmentStore: DepartmentStore,
        private blueUserStore: BlueUserStore
    ) {
        makeAutoObservable(this)
    }

    init = (userId: number) => {
        if (this.userId !== userId) {
            this.nearExpirationDismissedThisSession = false
        }
        this.userId = userId
    }

    dismissNearExpiration = () => {
        if (this.isExpired) return
        this.nearExpirationDismissedThisSession = true
    }

    get departmentsAccess() {
        return this.departmentsAccessStore.find(da => da.blueUserId === this.userId)
    }

    get passwordTTLdays() {
        if (!this.userId) return undefined

        const user = this.blueUserStore.findById(this.userId)
        if (!user) return undefined

        if (user.auth0 && user.auth0.enterprise) return undefined

        const shortestPasswordTTLdays = this.minimumDepartmentPasswordTTLdays
        if (shortestPasswordTTLdays === undefined) return undefined

        const passwordCreatedDate = moment(user.passwordCreatedDate)
        const daysSinceCreated = moment().diff(passwordCreatedDate, 'days')

        return shortestPasswordTTLdays - daysSinceCreated
    }

    get minimumTTLDepartment() {
        let relevantDepartmentIds = this.departmentsAccess
            ?.filter(da => !da.roles?.includes('guest'))
            .map(da => da.departmentId) ?? []

        // if user doesn't have a non-guest department, then
        // limit the password to lowest TTL department they are guest in
        if (!relevantDepartmentIds.length) {
            relevantDepartmentIds = this.departmentsAccess
                .map(da => da.departmentId) ?? []
        }

        const departments = this.departmentStore.findByIds(relevantDepartmentIds)

        // Filter to only departments with active TTL values
        const departmentsWithActiveTTL = departments.filter(d =>
            d.passwordTTL !== undefined && d.passwordTTL > 0
        )

        if (!departmentsWithActiveTTL.length) return undefined

        // Sort by TTL ascending and return the first one (with shortest TTL)
        return departmentsWithActiveTTL.sort((a, b) =>
            (a.passwordTTL as number) - (b.passwordTTL as number)
        )[0]
    }

    get minimumDepartmentPasswordTTLdays() {
        const minTTLDepartment = this.minimumTTLDepartment

        if (!minTTLDepartment || minTTLDepartment.passwordTTL === undefined) return undefined

        // convert to days
        return Math.floor(minTTLDepartment.passwordTTL / (3600 * 24))
    }

    // New computed properties to eliminate redundancy
    get isExpired() {
        const daysBeforeExpires = this.passwordTTLdays
        return (daysBeforeExpires !== undefined) && daysBeforeExpires < 1
    }

    get neverExpires() {
        return this.passwordTTLdays === undefined
    }

    get nearExpiration() {
        if (this.isExpired) return true
        if (this.nearExpirationDismissedThisSession) return false
        const daysBeforeExpires = this.passwordTTLdays
        return (daysBeforeExpires !== undefined) && daysBeforeExpires < 5
    }
}

let i = 0
decorate(injectable(), ResetPasswordController)
decorate(inject(DepartmentsAccessStore), ResetPasswordController, i++)
decorate(inject(DepartmentStore), ResetPasswordController, i++)
decorate(inject(BlueUserStore), ResetPasswordController, i++)

export { ResetPasswordController }
